// ✅ src/pages/VehicleV2/VehiclewiseReport_Print.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

// ✅ correct api for vehiclewise report
const API_BASE = "http://nissi-india.com/api/dieselConsumption";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

// keep whatever date format your server expects
const safeDate = (v) => {
  const s = toStr(v);
  return s;
};

export default function VehiclewiseReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ support BOTH state + querystring
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const passed = location?.state || {};

  // ✅ Build params from URL first, then state fallback
  const filters = useMemo(() => {
    // URL params (your current URL keys)
    const q = {
      vehtype: toStr(qs.get("vehtype")),
      branch: toStr(qs.get("branch")),
      vehRegNo: toStr(qs.get("vehRegNo")),
      month: toStr(qs.get("month")),
      year: toStr(qs.get("year")),
      fromDate: safeDate(qs.get("fromDate")),
      toDate: safeDate(qs.get("toDate")),
      vehicleMainType: toStr(qs.get("vehicleMainType")),
      vehicleName: toStr(qs.get("vehicleName")),
      vehicleCode: toStr(qs.get("vehicleCode")),
      logvehicleid: toStr(qs.get("logvehicleid")),
      logvehiclecode: toStr(qs.get("logvehiclecode")),
      logvehicledetid: toStr(qs.get("logvehicledetid")),
      logvehiclebranchid: toStr(qs.get("logvehiclebranchid")),
      logvehicleregno: toStr(qs.get("logvehicleregno")),
    };

    // Clean Select values
    const clean = (v) => (isSelect(v) ? "" : toStr(v));

    // FINAL report params (match your ASPX SetParameterValue names as close as possible)
    const finalParams = {
      // crystal params
      vehtype: clean(q.vehtype) || clean(passed?.vehtype ?? passed?.vehicletype ?? passed?.vehicletypeText),
      branch: clean(q.branch) || clean(passed?.branch ?? passed?.vehiclebranch),
      vehbasis: clean(passed?.vehbasis ?? passed?.vehiclebasis) || clean(q.vehicleMainType), // if your report uses basis, else keep empty
      month: clean(q.month) || clean(passed?.month ?? passed?.vehiclemonth),
      year: clean(q.year) || clean(passed?.year ?? passed?.vehicleyear),

      fromdate: safeDate(q.fromDate) || safeDate(passed?.fromdate ?? passed?.vehcilefromdate ?? passed?.fromDate),
      todate: safeDate(q.toDate) || safeDate(passed?.todate ?? passed?.vehciletodate ?? passed?.toDate),

      vehregno: clean(q.vehRegNo) || clean(passed?.vehregno ?? passed?.vehicleregno ?? passed?.regno),

      // your screen fields
      fld_vehicle_code: clean(q.vehicleCode) || clean(passed?.vehiclecode ?? passed?.fld_vehicle_code ?? passed?.veh_code),
      fld_vehicle_name: clean(q.vehicleName) || clean(passed?.vehiclename ?? passed?.fld_vehicle_name ?? passed?.veh_name),

      // if you have type id in state
      fld_vehicletypeid: clean(passed?.vehicletypeid ?? passed?.vehicleTypeid ?? passed?.fld_vehicletypeid),

      // EXTRA: send original URL keys too (backend can use whichever it wants)
      vehRegNo: clean(q.vehRegNo),
      fromDate: safeDate(q.fromDate),
      toDate: safeDate(q.toDate),
      vehicleMainType: clean(q.vehicleMainType),
      vehicleCode: clean(q.vehicleCode),
      vehicleName: clean(q.vehicleName),
      logvehicleid: clean(q.logvehicleid),
      logvehiclecode: clean(q.logvehiclecode),
      logvehicledetid: clean(q.logvehicledetid),
      logvehiclebranchid: clean(q.logvehiclebranchid),
      logvehicleregno: clean(q.logvehicleregno),

      // optional
      loginuserid: Number(sessionStorage.getItem("loginuserid")) || 0,
      branchType: toStr(sessionStorage.getItem("branchtype") || passed?.branchType),
    };

    return finalParams;
  }, [qs, passed]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const hasAnyParam = useMemo(() => {
    // if at least one meaningful filter exists, allow fetch
    return (
      !!toStr(filters.vehtype) ||
      !!toStr(filters.branch) ||
      !!toStr(filters.month) ||
      !!toStr(filters.year) ||
      !!toStr(filters.fromdate) ||
      !!toStr(filters.todate) ||
      !!toStr(filters.vehregno) ||
      !!toStr(filters.fld_vehicle_code) ||
      !!toStr(filters.fld_vehicle_name) ||
      !!toStr(filters.vehicleMainType)
    );
  }, [filters]);

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      if (!hasAnyParam) {
        throw new Error("Print params missing. Open print from Vehiclewise Report page.");
      }

      // ✅ Use the correct endpoint for this report
      // If your backend uses /list, keep /list. If it uses /print, change to /print.
      const { data } = await axios.get(`${API_BASE}/list`, { params: filters });

      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* ---------------- column mapping ---------------- */
  const getRegNo = (r) => pick(r, ["vehregno", "vehRegNo", "fld_vehicle_regno", "vehicle_regno", "RegNo", "regno"], "-");
  const getVehicleCode = (r) => pick(r, ["fld_vehicle_code", "vehiclecode", "veh_code", "VehicleCode", "vehicleCode"], "-");
  const getVehicleName = (r) => pick(r, ["fld_vehicle_name", "vehiclename", "veh_name", "VehicleName", "vehicleName"], "-");
  const getBranch = (r) => pick(r, ["branch", "BranchName", "fld_branchname", "branchname"], "-");
  const getVehType = (r) => pick(r, ["vehtype", "VehicleType", "fld_vehicletypename", "vehicletype"], "-");
  const getBasis = (r) => pick(r, ["vehbasis", "VehicleBasis", "basis", "fld_vehiclebasis", "vehicleMainType"], "-");
  const getDate = (r) => pick(r, ["date", "FillingDate", "fld_date", "entrydate", "EntryDate"], "");
  const getDieselQty = (r) => pick(r, ["diesel_qty", "DieselQty", "fld_dieselqty", "qty", "Quantity"], "");
  const getAmount = (r) => pick(r, ["amount", "Amount", "fld_amount", "total_amount", "Total"], "");

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
     
      
        <div className="w-full bg-white">
          <style>{`
            @page { size: A4 portrait; margin: 10mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `Records: ${rows.length}`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>
            </div>
          </div>

          <div className="print-area mx-auto max-w-[820px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">VEHICLE WISE REPORT</div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Vehicle Type</span> : {filters.vehtype || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {filters.branch || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Basis</span> : {filters.vehbasis || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Month / Year</span> : {filters.month || "-"} / {filters.year || "-"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div>
                        <span className="font-semibold">From</span> : {filters.fromdate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">To</span> : {filters.todate || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Reg No</span> : {filters.vehregno || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle</span> : {filters.fld_vehicle_code || "-"} /{" "}
                        {filters.fld_vehicle_name || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="p-2">
                  <table className="w-full border border-black border-collapse text-[12px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1 w-[6%]">S.No</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Reg No</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Veh Code</th>
                        <th className="border border-black px-2 py-1 w-[16%]">Vehicle Name</th>
                        <th className="border border-black px-2 py-1 w-[12%]">Branch</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Type</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Basis</th>
                        <th className="border border-black px-2 py-1 w-[10%]">Date</th>
                        <th className="border border-black px-2 py-1 w-[7%]">Diesel Qty</th>
                        <th className="border border-black px-2 py-1 w-[7%]">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={10}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={pick(r, ["id", "fld_id", "RowId"], `${idx}`)}>
                            <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-2 py-1">{getRegNo(r)}</td>
                            <td className="border border-black px-2 py-1">{getVehicleCode(r)}</td>
                            <td className="border border-black px-2 py-1">{getVehicleName(r)}</td>
                            <td className="border border-black px-2 py-1">{getBranch(r)}</td>
                            <td className="border border-black px-2 py-1">{getVehType(r)}</td>
                            <td className="border border-black px-2 py-1">{getBasis(r)}</td>
                            <td className="border border-black px-2 py-1">{getDate(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getDieselQty(r) || "-"}</td>
                            <td className="border border-black px-2 py-1 text-right">{getAmount(r) || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            {/* Debug */}
            <div className="no-print mt-4 text-xs text-gray-600">
              <details>
                <summary className="cursor-pointer">Debug: Query + Filters</summary>
                <pre className="mt-2 p-3 border rounded bg-gray-50 overflow-auto">
                  {JSON.stringify(
                    {
                      urlSearch: location.search,
                      filters,
                      rowsCount: rows.length,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
  
    </div>
  );
}